import * as React from 'react'
import styled from 'styled-components'
import { useEvent, useStore } from 'effector-react/ssr'
import { debounce } from 'debounce'
import * as musicPlayer from '@features/music-player/model'
import { Layout } from '@ui/layout'
import { TrackCard } from '@features/music-player/track-card'
import { OpenedTrackModal } from '@features/music-player/track-modal'
import { Devices } from '@styles'
import { matchMedia, MediaSource } from '@lib/image-media'
import { Social } from '@ui/social'
import musicDesktop from '@assets/images/music-page-cover.jpg'
import musicMobile from '@assets/images/music-cover-mobile.jpg'
import { isClientSide } from '@lib/ssr'
import { useBackgroundOnKroll } from '@lib/use-on-krol'
import styles from './music.module.scss'

const musicMedia: MediaSource<string>[] = [
  { mediaQuery: Devices.laptop, source: musicDesktop },
  { mediaQuery: Devices.mobileS, source: musicMobile },
]

export const MusicPage = () => {
  const [pageCover, setPageCover] = React.useState('')
  const showBackground = useBackgroundOnKroll()

  const playingTrackId = useStore(musicPlayer.$playingTrackId)
  const tracks = useStore(musicPlayer.$tracks)

  const trackCoverClicked = useEvent(musicPlayer.trackCoverClicked)
  const playClicked = useEvent(musicPlayer.playClicked)
  const pauseClicked = useEvent(musicPlayer.pauseClicked)

  const coverStyles: React.CSSProperties = { backgroundImage: `url(${pageCover})` }

  const onResize = React.useCallback(
    debounce(() => setPageCover(matchMedia(musicMedia, '')), 100),
    [],
  )

  React.useEffect(() => {
    if (!isClientSide()) {
      return
    }

    setPageCover(matchMedia(musicMedia, ''))
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <Layout showBorder={showBackground} className={styles.musicPage}>
      <MusicCover style={coverStyles} />

      <OpenedTrackModal />

      <Social color='white' />

      <PageTitle>Музыка</PageTitle>

      <Content>
        <TracksList>
          {tracks.map((track) => (
            <TrackCard
              key={track.id}
              trackId={track.id}
              coverUrl={track.coverUrl}
              trackTitleUrl={track.trackTitleUrl}
              year={track.year}
              artist={track.artist}
              title={track.title}
              isPlayingNow={playingTrackId === track.id}
              playClicked={playClicked}
              pauseClicked={pauseClicked}
              trackCoverClicked={trackCoverClicked}
            />
          ))}
        </TracksList>
      </Content>
    </Layout>
  )
}

const MusicCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;

  width: 100vw;
  height: 100vh;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const PageTitle = styled.h1`
  font-family: 'Magnolia Script', 'SansSerif';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 40px;

  margin: 0;

  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: 25px;

  @media screen and ${Devices.laptop} {
    font-size: 80px;
    line-height: 112px;

    margin-bottom: 40px;
    margin-left: 20vw;
  }

  @media screen and ${Devices.desktopS} {
    margin-left: 30vw;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;

  @media screen and ${Devices.laptop} {
    display: block;
    flex-direction: unset;
    align-items: unset;
    padding: unset;

    margin-left: 20vw;
  }

  @media screen and ${Devices.desktopS} {
    margin-left: 30vw;
  }
`

const TracksList = styled.div`
  @media screen and ${Devices.laptop} {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    gap: 30px;
  }

  @media screen and ${Devices.desktopS} {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    gap: 30px;
  }

  @media screen and ${Devices.desktopM} {
    grid-template-columns: repeat(4, max-content);
  }
`
